import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ArrowRight, ShieldCheck, Lock, Plus as PlusIcon, Sparkles } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useCnhCheckStore } from "@/stores/cnhCheckStore";
import { toast } from "sonner";
import { formatCurrency } from "@/api/client";
import { findTokenProduct, isOnlineEmission, loadCatalogProducts } from "@/lib/catalog";
import { ASSETS } from "@/lib/assets";
import { CHECKOUT_ORIGIN_KEY } from "@/hooks/useBuyNow";
import type { Product } from "@/types/api";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [tokenProduct, setTokenProduct] = useState<Product | null>(null);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const { items, addProduct, updateQuantity, removeItem, totalItems } = useCartStore();
  const requestCnhCheck = useCnhCheckStore((s) => s.request);

  const itemsCount = totalItems();
  const subtotal = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);

  // Cross-sell: load catalog on first open so we can suggest token products
  // and also run the CNH guard against online certificates later.
  useEffect(() => {
    if (!isOpen || catalog.length > 0) return;
    void loadCatalogProducts()
      .then((products) => {
        setCatalog(products);
        setTokenProduct(findTokenProduct(products) ?? null);
      })
      .catch(() => undefined);
  }, [isOpen, catalog.length]);

  const goToCheckout = () => {
    // Find online-emission items in the cart. We need the full Product
    // objects (not the CartItem shape) to run the CNH dialog, so we look
    // each up in the cached catalog.
    const onlineProducts = items
      .map((i) => catalog.find((p) => p.id === i.productId))
      .filter((p): p is Product => !!p && isOnlineEmission(p));

    if (onlineProducts.length === 0) {
      window.location.href = "/checkout";
      return;
    }

    requestCnhCheck(onlineProducts, (finalList, resolution) => {
      if (resolution === "canceled") return;

      if (resolution === "switched-presential") {
        // Replace each online item in the cart with its presential equivalent
        onlineProducts.forEach((online, idx) => {
          const presential = finalList[idx];
          if (presential && presential.id !== online.id) {
            const qty = items.find((i) => i.productId === online.id)?.quantity ?? 1;
            removeItem(online.id);
            addProduct(presential, qty);
          }
        });
        toast.success("Trocamos pelo equivalente presencial");
      }

      window.location.href = "/checkout";
    });
  };

  const hasA3 = items.some((i) => /A3/i.test(i.name) || /A3/i.test(i.sku));
  const hasToken = items.some((i) => /token/i.test(i.name) || /token/i.test(i.sku));
  const showTokenSuggestion = hasA3 && !hasToken && tokenProduct;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <ShoppingCart className="h-5 w-5" />
          {itemsCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary">
              {itemsCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <Link
            to={(typeof window !== "undefined" && sessionStorage.getItem(CHECKOUT_ORIGIN_KEY)) || "/"}
            onClick={() => setIsOpen(false)}
            aria-label="Voltar à página inicial"
            className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity w-fit mb-1"
          >
            <img
              src={ASSETS.faviconPng}
              alt="Biz Certificadora Digital"
              width={28}
              height={28}
              className="h-7 w-7 object-contain"
            />
            <span className="text-sm font-semibold text-foreground">Biz Certificadora Digital</span>
          </Link>
          <SheetTitle className="text-left">Carrinho de compras</SheetTitle>
          <SheetDescription className="text-left">
            {itemsCount === 0 ? "Seu carrinho está vazio" : `${itemsCount} ${itemsCount === 1 ? 'item' : 'itens'} no carrinho`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Seu carrinho está vazio</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = "/produtos";
                  }}
                >
                  Ver Produtos
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.productId} className="flex gap-4 p-3 border rounded-lg bg-card">
                      <div className="w-16 h-16 bg-muted rounded-md overflow-hidden flex-shrink-0">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-muted">
                            <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm">{item.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.sku}
                        </p>
                        {(() => {
                          // Resolve catalog product to show original vs effective price
                          const catalogProduct = catalog.find((p) => p.id === item.productId);
                          const hasPromo =
                            catalogProduct?.originalPriceCents &&
                            catalogProduct.originalPriceCents > catalogProduct.priceCents;
                          return (
                            <div className="mt-2">
                              {hasPromo && (
                                <p className="text-[10px] text-muted-foreground line-through tabular-nums">
                                  {formatCurrency(catalogProduct!.originalPriceCents!)}
                                </p>
                              )}
                              <p className="font-semibold text-sm tabular-nums text-foreground">
                                {formatCurrency(item.priceCents)}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => {
                            removeItem(item.productId);
                            toast.success("Item removido do carrinho");
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Cross-sell: token suggestion when A3 in cart */}
              {showTokenSuggestion && tokenProduct && (
                <div className="flex-shrink-0 mt-4 rounded-xl border-2 border-dashed border-warning/40 bg-warning/5 p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning/15">
                      <Sparkles className="h-4 w-4 text-warning" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground uppercase tracking-wider">
                        Quem comprou A3 também levou
                      </p>
                      <p className="text-sm font-semibold text-foreground mt-1 line-clamp-1">
                        {tokenProduct.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(tokenProduct.priceCents)} · necessário pra usar o A3
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          addProduct(tokenProduct, 1);
                          toast.success("Token adicionado ao carrinho");
                        }}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-warning hover:underline"
                      >
                        <PlusIcon className="h-3 w-3" /> Adicionar token ao pedido
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Fixed checkout section */}
              <div className="flex-shrink-0 space-y-3 pt-6 border-t bg-background">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Subtotal</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(subtotal)}
                  </span>
                </div>

                <Button
                  onClick={goToCheckout}
                  className="w-full bg-success hover:bg-success/90 font-bold shadow-md"
                  size="lg"
                  disabled={items.length === 0}
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Finalizar compra com segurança
                </Button>

                {/* Trust badges in cart */}
                <div className="flex items-center justify-center gap-3 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3 text-success" /> Pagamento seguro
                  </span>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3 text-success" /> ICP-Brasil
                  </span>
                </div>

                <p className="text-[11px] text-center text-muted-foreground">
                  O frete fixo para token será calculado automaticamente no checkout
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
